import React, { useEffect, useState } from "react";
import {
    Box, Button, Typography, List, ListItem,
    ListItemText, ListItemIcon, CircularProgress,
    InputLabel, FormControl
} from "@mui/material";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DownloadIcon from '@mui/icons-material/Download';
import { UrlConstant } from "../../../../commom/UrlConstant";
import Axios from "../../../../config/axios";
import moment from "moment";
import DatePicker from "react-datepicker";
import Successpopup from "../../SharedComponent/Successpopup";
import { useInfiniteScroll } from "../../../../hooks/useInfiniteScroll";
import { useScrollStyles } from "../../../../hooks/useScrollStyles";
import { InfiniteScrollLoader } from "../../SharedComponent/InfiniteScrollLoader";
import { usePriceFormatter } from "../../../../hooks";
import { useDocumentPassword } from "../../../../commom/hooks/useDocumentPassword";

const TradeConfirmations: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [documentList, setDocumentsList] = useState<any[]>([]);
    const [startDate, setStartDate] = useState<any>(moment().subtract(30, "days").toDate());
    const [endDate, setEndDate] = useState<any>(moment().toDate());
    const [popup, setPopup] = useState({ show: false, success: true, heading: '', message: '' });
    const formatter = usePriceFormatter({ forceSeparator: true });
    const { fetchPassword } = useDocumentPassword();
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

    const scrollStyles = useScrollStyles('#F2674A');

    const handleViewPDF = async (item: any) => {
        if (!item.s3Path) return;

        const pwd = await fetchPassword();
        setPendingAction(() => () => window.open(item.s3Path, '_blank'));
        setPopup({
            show: true,
            success: true,
            heading: 'Document Password',
            message: pwd
                ? `This document is password protected. Your PDF password is: ${pwd}`
                : 'This document is password protected — find your password under Account Settings.',
        });
    };

    const getTradeConfirmationsList = async () => {
        setLoading(true);
        try {
            const response = await Axios.post(UrlConstant.Documents.TradeConfirmations, {
                startDate: moment(startDate).format("YYYY-MM-DD"),
                endDate: moment(endDate).format("YYYY-MM-DD"),
            });
            const resData = response.data;
            const allItems = (resData?.data ?? []).flatMap((group: any) => group.items ?? []);
            setDocumentsList(allItems);
        } catch (error: any) {
            console.error("Error fetching trade confirmations:", error);
            setDocumentsList([]);
            setPopup({ show: true, success: false, heading: 'Error', message: 'Failed to fetch trade confirmations.' });
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadBulk = async () => {
        if (!startDate || !endDate) {
            setPopup({ show: true, success: false, heading: 'Warning', message: 'Please select both start and end dates' });
            return;
        }

        setLoading(true);
        try {
            const response = await Axios.post(
                UrlConstant.Documents.TradeConfirmationsBulkPDF,
                {
                    startDate: moment(startDate).format("YYYY-MM-DD"),
                    endDate: moment(endDate).format("YYYY-MM-DD"),
                },
                { responseType: "blob" }
            );

            const disposition = response.headers["content-disposition"];
            let filename = `Wolfpack-Trade-Confirmations-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}.pdf`;
            if (disposition) {
                const match = disposition.match(/filename="?([^"]+)"?/);
                if (match?.[1]) filename = match[1];
            }

            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            const pwd = await fetchPassword();
            setPopup({
                show: true,
                success: true,
                heading: 'Success',
                message: pwd
                    ? `Download successful. Your PDF password is: ${pwd}`
                    : 'Download successful. This PDF is password protected — find your password under Account Settings.',
            });
        } catch (error: any) {
            console.error('Download error:', error);
            setPopup({
                show: true,
                success: false,
                heading: 'Error',
                message: error.response?.data?.message || 'Failed to download trade confirmations'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getTradeConfirmationsList();
    }, [startDate, endDate]);

    const {
        displayedData: displayedItems,
        hasMore,
        loadingMore,
        scrollContainerRef,
        observerTarget,
    } = useInfiniteScroll({
        data: documentList,
        initialItemsPerLoad: 10,
        itemsPerLoad: 10,
    });

    const groupedDisplayed: Record<string, { monthYear: string; items: any[] }> =
        displayedItems.reduce((acc, doc: any) => {
            const monthYear = moment(doc.date).format("MMMM YYYY");
            if (!acc[monthYear]) acc[monthYear] = { monthYear, items: [] };
            acc[monthYear].items.push(doc);
            return acc;
        }, {} as any);

    return (
        <>
            {loading && (
                <Box position="fixed" top={0} left={0} width="100vw" height="100vh"
                    bgcolor="rgba(0,0,0,0.5)" display="flex" justifyContent="center"
                    alignItems="center" style={{ backdropFilter: "blur(5px)", zIndex: 9999 }}>
                    <CircularProgress color="primary" />
                </Box>
            )}

            <Box sx={{
                flex: 1,
                backgroundColor: "#FFFFFF1A",
                borderRadius: '20px',
                border: "1px solid #FFFFFF2A",
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                maxHeight: 'calc(100vh - 200px)',
            }}>
                {/* Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: '20px' }}>
                    <Typography variant="h5">Trade Confirmations</Typography>
                    <Button variant="contained" disabled={!startDate || !endDate || loading}
                        onClick={handleDownloadBulk} startIcon={<DownloadIcon />}
                        sx={{
                            backgroundColor: "#F2674A", borderRadius: "12px",
                            padding: "10px 24px", fontSize: "14px", textTransform: "none",
                            "&:hover": { backgroundColor: "#d9543a" },
                            "&:disabled": { backgroundColor: "#FFFFFF2A", color: "#FFFFFF5A" }
                        }}>
                        {loading ? <CircularProgress size={18} color="inherit" /> : "Download All"}
                    </Button>
                </Box>

                {/* Date Pickers - unchanged */}
                <Box display="flex" justifyContent="space-between" gap={2} mb="20px">
                    <FormControl fullWidth sx={{ mt: "20px" }}>
                        <InputLabel shrink={false} sx={{ color: "gray !important" }}>
                            Start Date <sup style={{ color: "red", marginLeft: "5px" }}>*</sup>
                        </InputLabel>
                        <DatePicker selected={startDate}
                            onChange={(date: Date | null) => {
                                setStartDate(date);
                                if (endDate && date && endDate < date) setEndDate(null);
                            }}
                            dateFormat="dd-MMMM-yyyy" placeholderText="Select a start date"
                            showIcon className="datePickerInput" wrapperClassName="datePickerWrapper"
                            showYearDropdown scrollableYearDropdown maxDate={new Date()} />
                    </FormControl>
                    <FormControl fullWidth sx={{ mt: "20px" }}>
                        <InputLabel shrink={false} sx={{ color: "gray !important" }}>
                            End Date <sup style={{ color: "red", marginLeft: "5px" }}>*</sup>
                        </InputLabel>
                        <DatePicker selected={endDate}
                            onChange={(date: Date | null) => setEndDate(date)}
                            dateFormat="dd-MMMM-yyyy" placeholderText="Select an end date"
                            showIcon className="datePickerInput" wrapperClassName="datePickerWrapper"
                            showYearDropdown scrollableYearDropdown
                            minDate={startDate || undefined} maxDate={new Date()} disabled={!startDate} />
                    </FormControl>
                </Box>

                <Box ref={scrollContainerRef} sx={scrollStyles}>
                    {documentList.length === 0 && !loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                            <Typography variant="h6" color="#FFFFFF5A">
                                No trade confirmations found for the selected date range
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {Object.values(groupedDisplayed).map((monthGroup, index) => (
                                <Box key={index} mb='30px'>
                                    <Typography variant="h5" mb='20px'>
                                        {monthGroup.monthYear}
                                    </Typography>
                                    <List sx={{ padding: 0 }}>
                                        {monthGroup.items.map((doc: any, docIndex: number) => (
                                            <ListItem key={`${doc._id}-${docIndex}`}
                                                sx={{
                                                    backgroundColor: "#FFFFFF2A",
                                                    borderRadius: '20px',
                                                    padding: '20px 30px',
                                                    minHeight: '113px',
                                                    marginBottom: 1,
                                                    cursor: "pointer",
                                                    transition: 'all 0.2s ease-in-out',
                                                    '&:hover': { backgroundColor: '#FFFFFF33', transform: 'translateX(5px)' }
                                                }}
                                                onClick={() => handleViewPDF(doc)}
                                            >
                                                <ListItemIcon sx={{ marginRight: '20px' }}>
                                                    <Box height='73px' width='73px' borderRadius='50px'
                                                        padding='10px 0px' textAlign='center' bgcolor='#FFFFFF1A'>
                                                        <Typography variant="h5" fontWeight='600' color="#F2674A" mb='0px'>
                                                            {moment(doc.date).format("DD")}
                                                        </Typography>
                                                        <Typography variant="subtitle2" color="#FFFFFF5A">
                                                            {moment(doc.date).format("MMM")}
                                                        </Typography>
                                                    </Box>
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Typography fontSize="20px">
                                                            {moment(doc.date).format("DD MMM, YYYY")}{" "}
                                                            <span style={{ color: '#4A7AF2' }}>
                                                                {moment(doc.date).format("hh:mm A")}
                                                            </span>
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography color="#FFFFFF" fontSize='16px'>
                                                            {doc.symbol} - {doc.displayType} - {formatter.quantity(doc.quantity).display} @ {formatter.amount(doc.totalAmount).display}
                                                        </Typography>
                                                    }
                                                />
                                                <ArrowForwardIosIcon sx={{ fontSize: "32px" }} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            ))}

                            <InfiniteScrollLoader
                                hasMore={hasMore}
                                loadingMore={loadingMore}
                                observerTarget={observerTarget}
                                dataLength={displayedItems.length}
                                endMessage="• All confirmations loaded •"
                            />
                        </>
                    )}
                </Box>
            </Box>

            {popup.show && (
                <Successpopup
                    onClose={() => {
                        setPopup({ ...popup, show: false });
                        if (pendingAction) {
                            pendingAction();
                            setPendingAction(null);
                        }
                    }}
                    Success={popup.success}
                    Heading={popup.heading}
                    Message={popup.message}
                />
            )}
        </>
    );
};

export default TradeConfirmations;